import React, { useState, useEffect } from "react";
import RefreshIcon from "@material-ui/icons/Refresh";
import { Accordion } from "../components/setting-accordion";
import { SaveButton } from "../components/save-button";
import { Toggle } from "../components/toggle";
import { saveHighLow2 } from "../../../../../../../api";
import { toDateString, getDaysSinceWeighIn } from "../../../util";
import SettingIcon from "../../../../../../../images/settings-icons/setting-tour.png";
import DayColumn from "./day-column";
import {
  Container,
  ResetContainer,
  ResetButton,
  WeekContainer,
  ControlGroup,
  ControlLabel,
  Label,
  ValueInput,
  ButtonRow,
  ErrorBox,
  ErrorIcon,
  CalorieSlider,
  MacroSlider,
  FatSlider,
} from "./styled-components";
import {
  getDefaultMacros,
  getCalorieLimits,
  initializeWeeklyData,
  getSelectedDaysValues,
  getMacroPercentages,
  doesDayMatchSelectedCalories,
  getMacroFlexValue,
  getMinCarbs,
  getMaxCarbs,
  getMinFat,
  getMaxFat,
  getSliderValue,
  getCalorieSliderBounds,
  validateInputs,
  canChangeCalories,
} from "./utils";

const UpdateHighLow = (props) => {
  const [isLoading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]); // Array of selected day indices, default to no days selected
  const [highLowEveryWeek, setHighLowEveryWeek] = useState(
    props.account.user.high_low_every_week || 0
  );
  const [inputErrors, setInputErrors] = useState([]);
  const [calorieInput, setCalorieInput] = useState(null);
  const [carbsInput, setCarbsInput] = useState(null);
  const [fatInput, setFatInput] = useState(null);
  const [frozenCalorieBounds, setFrozenCalorieBounds] = useState(null);

  // Get protein value (always the same, not stored per day)
  const userProtein = props.account.user.last_protein || props.account.user.protein || 150;

  // Helper functions that use props
  const getDefaultMacrosFn = () => getDefaultMacros(props.account.user);
  const getCalorieLimitsFn = () => getCalorieLimits(props.account.user, getDefaultMacrosFn);
  const daysAfterLastCheckIn = getDaysSinceWeighIn(props.lastWeighInDate);

  // Convert lastWeighInDate string to Date for calculations (using UTC to avoid timezone issues)
  const lastWeighInDateTemp = new Date(props.lastWeighInDate);
  const lastWeighInDate = new Date(lastWeighInDateTemp.getUTCFullYear(), lastWeighInDateTemp.getUTCMonth(), lastWeighInDateTemp.getUTCDate());

  // Initialize weekly data
  const [weeklyData, setWeeklyData] = useState(() => {
    return initializeWeeklyData(
      props.account.user,
      lastWeighInDate,
      props.macroData,
      getDefaultMacrosFn
    );
  });

  // Re-initialize weekly data when account or macroData changes
  useEffect(() => {
    const newWeeklyData = initializeWeeklyData(
      props.account.user,
      lastWeighInDate,
      props.macroData,
      getDefaultMacrosFn
    );
    setWeeklyData(newWeeklyData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.account?.user?.high_low_plan, props.account?.user?.last_carbs, props.account?.user?.last_fat, props.macroData]);

  // Ensure past days are always locked and deselected
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let needsUpdate = false;
    const newWeeklyData = { ...weeklyData };
    const pastDayIndices = [];
    
    for (let i = 0; i < newWeeklyData.days.length; i++) {
      const dayDate = new Date(lastWeighInDate);
      dayDate.setDate(dayDate.getDate() + i);
      dayDate.setHours(0, 0, 0, 0);
      const isPast = dayDate < today;
      
      if (isPast) {
        pastDayIndices.push(i);
        if (!newWeeklyData.days[i].locked) {
          newWeeklyData.days[i].locked = true;
          needsUpdate = true;
        }
      }
    }
    
    // Deselect any past days that are currently selected
    if (pastDayIndices.length > 0 && selectedDays.some(index => pastDayIndices.includes(index))) {
      setSelectedDays(prev => prev.filter(index => !pastDayIndices.includes(index)));
    }
    
    if (needsUpdate) {
      setWeeklyData(newWeeklyData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastWeighInDate]);

  // Get values for selected days - if multiple selected, calculate average/min/max
  const selectedDaysValues = getSelectedDaysValues(selectedDays, weeklyData);
  const currentDay = selectedDays.length === 1 
    ? weeklyData.days[selectedDays[0]] 
    : null;
  const computedCalorieBounds = getCalorieSliderBounds(
    selectedDays,
    weeklyData,
    getCalorieLimitsFn,
    props.account.user,
    getDefaultMacrosFn
  );
  const calorieBounds = frozenCalorieBounds || computedCalorieBounds;
  const displayedComputedCalorieBounds = {
    min: Math.round(computedCalorieBounds.min),
    max: Math.round(computedCalorieBounds.max),
  };
  const displayedCalorieBounds = {
    min: Math.round(calorieBounds.min),
    max: Math.round(calorieBounds.max),
  };
  const lockedStateKey = weeklyData.days.map((day) => (day.locked ? 1 : 0)).join(",");

  useEffect(() => {
    setFrozenCalorieBounds(null);
  }, [selectedDays, lockedStateKey, props.account.user.custom_calorie_floor]);

  const handleDaySelect = (dayIndex) => {
    // Don't allow selecting past days
    const dayDate = new Date(lastWeighInDate);
    dayDate.setDate(dayDate.getDate() + dayIndex);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dayDate.setHours(0, 0, 0, 0);
    const isPast = dayDate < today;
    
    if (isPast) {
      return;
    }
    
    // Don't allow switching if there are errors
    if (inputErrors.length > 0) {
      props.openSnackbar(
        "Please fix errors before switching to another day",
        true
      );
      return;
    }
    
    // Single selection only - clicking a day selects it (replaces previous selection)
    setSelectedDays([dayIndex]);
    
    // Clear errors and input values when switching days
    setInputErrors([]);
    setCalorieInput(null);
    setCarbsInput(null);
    setFatInput(null);
  };

  const handleCheckboxToggle = (dayIndex, event) => {
    // Stop event propagation to prevent day selection
    event.stopPropagation();
    
    // Don't allow checkbox toggle for past days
    const dayDate = new Date(lastWeighInDate);
    dayDate.setDate(dayDate.getDate() + dayIndex);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dayDate.setHours(0, 0, 0, 0);
    const isPast = dayDate < today;
    
    if (isPast) {
      return;
    }
    
    // Don't allow checkbox toggle if there are errors
    if (inputErrors.length > 0) {
      props.openSnackbar(
        "Please fix errors before changing selection",
        true
      );
      return;
    }
    
    // Toggle day in multi-selection
    setSelectedDays(prev => {
      if (prev.length === 0) {
        // If no days selected, select this day as primary
        return [dayIndex];
      }
      
      if (prev.includes(dayIndex)) {
        // Deselect if already selected, but keep at least one day selected
        const newSelection = prev.filter(i => i !== dayIndex);
        return newSelection.length > 0 ? newSelection : prev;
      } else {
        // Add to selection, but limit to 6 days maximum
        if (prev.length >= 6) {
          props.openSnackbar(
            "Maximum 6 days can be selected at a time",
            true
          );
          return prev;
        }
        return [...prev, dayIndex];
      }
    });
    
    // Clear errors and input values when changing selection
    setInputErrors([]);
    setCalorieInput(null);
    setCarbsInput(null);
    setFatInput(null);
  };

  const handleDayLock = (dayIndex) => {
    // Don't allow unlocking past days
    const dayDate = new Date(lastWeighInDate);
    dayDate.setDate(dayDate.getDate() + dayIndex);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dayDate.setHours(0, 0, 0, 0);
    const isPast = dayDate < today;
    
    // If trying to unlock a past day, don't allow it
    if (isPast && !weeklyData.days[dayIndex].locked) {
      return;
    }
    
    const newWeeklyData = { ...weeklyData };
    newWeeklyData.days[dayIndex].locked = !newWeeklyData.days[dayIndex].locked;
    setWeeklyData(newWeeklyData);
  };

  const handleInput = (type, value) => {
    // Don't allow input if no days are selected
    if (selectedDays.length === 0) {
      return;
    }

    const numValue =
      type === "calories" ? parseInt(value) || 0 : parseFloat(value) || 0;

    // Store input value for display
    if (type === "calories") {
      setCalorieInput(numValue);
    } else if (type === "carbs") {
      setCarbsInput(numValue);
    } else if (type === "fat") {
      setFatInput(numValue);
    }

    // Calculate adjusted macro values (for carbs/fat changes, the other macro adjusts)
    let adjustedCarbs = numValue;
    let adjustedFat;
    let caloriesForValidation;

    if (type === "carbs") {
      // When carbs change, fat adjusts to maintain calories
      if (selectedDays.length === 1) {
        const day = weeklyData.days[selectedDays[0]];
        const currentCalories = userProtein * 4 + day.carbs * 4 + day.fat * 9;
        const calorieDifference = (numValue - day.carbs) * 4;
        const fatAdjustment = -calorieDifference / 9;
        adjustedFat = Math.max(0, day.fat + fatAdjustment);
        caloriesForValidation = day.calories;
      } else {
        const avgCarbs = selectedDaysValues.carbs.avg;
        const avgFat = selectedDaysValues.fat.avg;
        const avgCalories = selectedDaysValues.calories.avg;
        const calorieDifference = (numValue - avgCarbs) * 4;
        const fatAdjustment = -calorieDifference / 9;
        adjustedFat = Math.max(0, avgFat + fatAdjustment);
        caloriesForValidation = avgCalories;
      }
    } else if (type === "fat") {
      // When fat changes, carbs adjust to maintain calories
      adjustedFat = numValue;
      if (selectedDays.length === 1) {
        const day = weeklyData.days[selectedDays[0]];
        const currentCalories = userProtein * 4 + day.carbs * 4 + day.fat * 9;
        const calorieDifference = (numValue - day.fat) * 9;
        const carbAdjustment = -calorieDifference / 4;
        adjustedCarbs = Math.max(0, day.carbs + carbAdjustment);
        caloriesForValidation = day.calories;
      } else {
        const avgCarbs = selectedDaysValues.carbs.avg;
        const avgFat = selectedDaysValues.fat.avg;
        const avgCalories = selectedDaysValues.calories.avg;
        const calorieDifference = (numValue - avgFat) * 9;
        const carbAdjustment = -calorieDifference / 4;
        adjustedCarbs = Math.max(0, avgCarbs + carbAdjustment);
        caloriesForValidation = avgCalories;
      }
    } else {
      // For calories, use current macro values
      if (selectedDays.length === 1) {
        const day = weeklyData.days[selectedDays[0]];
        adjustedCarbs = day.carbs;
        adjustedFat = day.fat;
        caloriesForValidation = numValue;
      } else {
        adjustedCarbs = selectedDaysValues.carbs.avg;
        adjustedFat = selectedDaysValues.fat.avg;
        caloriesForValidation = numValue;
      }
    }

    // Validate with adjusted values
    let errors;
    if (selectedDays.length === 1) {
      const day = weeklyData.days[selectedDays[0]];
      errors = validateInputs(caloriesForValidation, adjustedCarbs, adjustedFat, userProtein, getCalorieLimitsFn, props.account.user, getDefaultMacrosFn, selectedDays, weeklyData, day, selectedDaysValues);
    } else {
      errors = validateInputs(caloriesForValidation, adjustedCarbs, adjustedFat, userProtein, getCalorieLimitsFn, props.account.user, getDefaultMacrosFn, selectedDays, weeklyData, currentDay, selectedDaysValues);
    }

    setInputErrors(errors);

    // Only update state (and trigger macro recalculation) if validation passes
    if (errors.length === 0) {
      if (type === "calories") {
        const didUpdate = handleCalorieChange(
          { target: { value: numValue } },
          displayedCalorieBounds
        );
        if (didUpdate) {
          setCalorieInput(null); // Clear input value to use state value
        }
      } else {
        handleMacroChange(type, { target: { value: numValue } });
        if (type === "carbs") {
          setCarbsInput(null);
        } else if (type === "fat") {
          setFatInput(null);
        }
      }
    }
  };

  const handleSlider = (type, value) => {
    // Don't allow slider changes if no days are selected
    if (selectedDays.length === 0) {
      return;
    }

    if (type === "calories") {
      if (!frozenCalorieBounds) {
        setFrozenCalorieBounds(displayedComputedCalorieBounds);
      }
      const activeBounds = frozenCalorieBounds || displayedComputedCalorieBounds;
      // For calories, check if change is valid before applying
      if (canChangeCalories(value, selectedDays, weeklyData, getCalorieLimitsFn, props.account.user, getDefaultMacrosFn, activeBounds)) {
        const didUpdate = handleCalorieChange({ target: { value } }, activeBounds);
        if (didUpdate) {
          setCalorieInput(null);
          // Clear errors when using slider
          setInputErrors([]);
        }
      }
      // If invalid, don't apply the change (slider will stay at previous valid value)
    } else {
      // Clear all input values when moving a macro slider (since it will adjust the other macro)
      setCarbsInput(null);
      setFatInput(null);
      // Clear errors when using slider (slider enforces bounds)
      setInputErrors([]);
      // Apply the change - this will adjust the other macro
      handleMacroChange(type, { target: { value } });
    }
  };

  const handleCalorieSliderCommit = () => {
    setFrozenCalorieBounds(null);
  };

  const handleCalorieChange = (event, calorieBoundsOverride = null) => {
    const newCalories = parseFloat(event.target.value) || 0;
    
    // Check if all available days are selected - if so, don't allow calorie changes
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const availableDays = [];
    for (let i = 0; i < weeklyData.days.length; i++) {
      const dayDate = new Date(lastWeighInDate);
      dayDate.setDate(dayDate.getDate() + i);
      dayDate.setHours(0, 0, 0, 0);
      const isPast = dayDate < today;
      if (!isPast && !weeklyData.days[i].locked) {
        availableDays.push(i);
      }
    }
    
    const allAvailableDaysSelected = availableDays.length > 0 && 
      availableDays.every(dayIndex => selectedDays.includes(dayIndex));
    
    if (allAvailableDaysSelected) {
      // All available days are selected - can't change calories as it would affect weekly total
      return false;
    }
    
    // Check if this change would cause any day to hit limits
    if (!canChangeCalories(newCalories, selectedDays, weeklyData, getCalorieLimitsFn, props.account.user, getDefaultMacrosFn, calorieBoundsOverride)) {
      // Don't allow the change - just prevent it (don't show error for slider drags)
      return false;
    }

    const newWeeklyData = { ...weeklyData };

    // Calculate total current calories for all selected days
    const totalCurrentCalories = selectedDays.reduce(
      (sum, index) => sum + newWeeklyData.days[index].calories,
      0
    );
    const totalNewCalories = newCalories * selectedDays.length;
    const totalCalorieDifference = totalNewCalories - totalCurrentCalories;

    // Update all selected days' calories and recalculate their macros
    selectedDays.forEach((dayIndex) => {
      const day = newWeeklyData.days[dayIndex];
      
      // Update calories
      day.calories = newCalories;

      // Recalculate carbs and fat for this day based on new calories while maintaining current ratio
      const currentProteinCalories = userProtein * 4;
      const currentCarbsCalories = day.carbs * 4;
      const currentFatCalories = day.fat * 9;
      const currentTotalCalories =
        currentProteinCalories + currentCarbsCalories + currentFatCalories;

      if (currentTotalCalories > 0) {
        // Calculate minimum constraints
        const minCarbs = getMinCarbs(
          selectedDays,
          weeklyData,
          day,
          selectedDaysValues,
          userProtein,
          getDefaultMacrosFn,
          props.account.user
        );
        const minFat = getMinFat(selectedDays, weeklyData, day, selectedDaysValues, getDefaultMacrosFn, props.account.user); // 15% of calories
        const minCarbsCalories = minCarbs * 4;
        const minFatCalories = minFat * 9;

        // Calculate the ratio of carbs and fat (excluding protein)
        const carbsFatCalories = currentCarbsCalories + currentFatCalories;
        const carbsRatio = currentCarbsCalories / carbsFatCalories;
        const fatRatio = currentFatCalories / carbsFatCalories;

        // Calculate new carbs and fat calories based on new total calories
        const newCarbsFatCalories = newCalories - currentProteinCalories;
        let newCarbsCalories = newCarbsFatCalories * carbsRatio;
        let newFatCalories = newCarbsFatCalories * fatRatio;

        // Enforce minimum constraints
        if (newCarbsCalories < minCarbsCalories) {
          // Carbs hit minimum, adjust fat
          newCarbsCalories = minCarbsCalories;
          newFatCalories = newCarbsFatCalories - newCarbsCalories;
        }
        
        if (newFatCalories < minFatCalories) {
          // Fat hit minimum, adjust carbs
          newFatCalories = minFatCalories;
          newCarbsCalories = newCarbsFatCalories - newFatCalories;
          
          // If carbs would go below minimum after fat adjustment, prioritize carbs
          if (newCarbsCalories < minCarbsCalories) {
            newCarbsCalories = minCarbsCalories;
            // Fat will be whatever is left, but ensure it's at least minimum
            newFatCalories = Math.max(minFatCalories, newCarbsFatCalories - newCarbsCalories);
          }
        }

        // Update carbs and fat for this day (protein stays the same)
        day.carbs = Math.max(minCarbs, newCarbsCalories / 4);
        day.fat = Math.max(minFat, newFatCalories / 9);
      }
    });

    // Redistribute the difference across other unlocked days (excluding all selected days)
    const unlockedDays = newWeeklyData.days.filter(
      (day, index) => !selectedDays.includes(index) && !day.locked
    );

    if (unlockedDays.length > 0 && totalCalorieDifference !== 0) {
      // Calculate how much to adjust each unlocked day (keep decimals for accuracy)
      const adjustmentPerDay = -totalCalorieDifference / unlockedDays.length;

      // Apply the adjustment to each unlocked day and recalculate macros
      // Note: We already validated that no day will hit limits, so we can apply directly
      newWeeklyData.days.forEach((day, index) => {
        if (!selectedDays.includes(index) && !day.locked) {
          const newDayCalories = day.calories + adjustmentPerDay;
          newWeeklyData.days[index].calories = newDayCalories;

          // Recalculate macros for this day based on new calories while maintaining ratios
          const currentProteinCalories = userProtein * 4;
          const currentCarbsCalories = day.carbs * 4;
          const currentFatCalories = day.fat * 9;
          const currentTotalCalories =
            currentProteinCalories + currentCarbsCalories + currentFatCalories;

          if (currentTotalCalories > 0) {
            // Calculate minimum constraints
            const minCarbs = getMinCarbs(
              [],
              weeklyData,
              day,
              null,
              userProtein,
              getDefaultMacrosFn,
              props.account.user
            );
            const minFat = getMinFat([], weeklyData, day, null, getDefaultMacrosFn, props.account.user); // 15% of calories
            const minCarbsCalories = minCarbs * 4;
            const minFatCalories = minFat * 9;

            // Calculate the ratio of carbs and fat (excluding protein)
            const carbsFatCalories = currentCarbsCalories + currentFatCalories;
            const carbsRatio = currentCarbsCalories / carbsFatCalories;
            const fatRatio = currentFatCalories / carbsFatCalories;

            // Calculate new carbs and fat calories based on new total calories
            const newCarbsFatCalories = newDayCalories - currentProteinCalories;
            let newCarbsCalories = newCarbsFatCalories * carbsRatio;
            let newFatCalories = newCarbsFatCalories * fatRatio;

            // Enforce minimum constraints
            if (newCarbsCalories < minCarbsCalories) {
              // Carbs hit minimum, adjust fat
              newCarbsCalories = minCarbsCalories;
              newFatCalories = newCarbsFatCalories - newCarbsCalories;
            }
            
            if (newFatCalories < minFatCalories) {
              // Fat hit minimum, adjust carbs
              newFatCalories = minFatCalories;
              newCarbsCalories = newCarbsFatCalories - newFatCalories;
              
              // If carbs would go below minimum after fat adjustment, prioritize carbs
              if (newCarbsCalories < minCarbsCalories) {
                newCarbsCalories = minCarbsCalories;
                // Fat will be whatever is left, but ensure it's at least minimum
                newFatCalories = Math.max(minFatCalories, newCarbsFatCalories - newCarbsCalories);
              }
            }

            // Update carbs and fat (protein stays the same)
            newWeeklyData.days[index].carbs = Math.max(minCarbs, newCarbsCalories / 4);
            newWeeklyData.days[index].fat = Math.max(minFat, newFatCalories / 9);
          }
        }
      });

      // Handle any remaining difference due to floating point precision
      const totalAdjustment = adjustmentPerDay * unlockedDays.length;
      const remainingDifference = -totalCalorieDifference - totalAdjustment;

      if (remainingDifference !== 0 && unlockedDays.length > 0) {
        // Apply remaining difference to the first unlocked day
        const firstUnlockedIndex = newWeeklyData.days.findIndex(
          (day, index) => !selectedDays.includes(index) && !day.locked
        );
        if (firstUnlockedIndex !== -1) {
          // Note: We already validated that no day will hit limits
          const finalCalories = newWeeklyData.days[firstUnlockedIndex].calories +
            remainingDifference;
          newWeeklyData.days[firstUnlockedIndex].calories = finalCalories;

          // Recalculate macros for this day too
          const day = newWeeklyData.days[firstUnlockedIndex];
          const currentProteinCalories = userProtein * 4;
          const currentCarbsCalories = day.carbs * 4;
          const currentFatCalories = day.fat * 9;
          const currentTotalCalories =
            currentProteinCalories + currentCarbsCalories + currentFatCalories;

          if (currentTotalCalories > 0) {
            // Calculate minimum constraints
            const minCarbs = getMinCarbs(
              [],
              weeklyData,
              day,
              null,
              userProtein,
              getDefaultMacrosFn,
              props.account.user
            );
            const minFat = getMinFat([], weeklyData, day, null, getDefaultMacrosFn, props.account.user); // 15% of calories
            const minCarbsCalories = minCarbs * 4;
            const minFatCalories = minFat * 9;

            const carbsFatCalories = currentCarbsCalories + currentFatCalories;
            const carbsRatio = currentCarbsCalories / carbsFatCalories;
            const fatRatio = currentFatCalories / carbsFatCalories;

            const newCarbsFatCalories = finalCalories - currentProteinCalories;
            let newCarbsCalories = newCarbsFatCalories * carbsRatio;
            let newFatCalories = newCarbsFatCalories * fatRatio;

            // Enforce minimum constraints
            if (newCarbsCalories < minCarbsCalories) {
              // Carbs hit minimum, adjust fat
              newCarbsCalories = minCarbsCalories;
              newFatCalories = newCarbsFatCalories - newCarbsCalories;
            }
            
            if (newFatCalories < minFatCalories) {
              // Fat hit minimum, adjust carbs
              newFatCalories = minFatCalories;
              newCarbsCalories = newCarbsFatCalories - newFatCalories;
              
              // If carbs would go below minimum after fat adjustment, prioritize carbs
              if (newCarbsCalories < minCarbsCalories) {
                newCarbsCalories = minCarbsCalories;
                // Fat will be whatever is left, but ensure it's at least minimum
                newFatCalories = Math.max(minFatCalories, newCarbsFatCalories - newCarbsCalories);
              }
            }

            newWeeklyData.days[firstUnlockedIndex].carbs = Math.max(
              minCarbs,
              newCarbsCalories / 4
            );
            newWeeklyData.days[firstUnlockedIndex].fat = Math.max(
              minFat,
              newFatCalories / 9
            );
          }
        }
      }
    }

    setWeeklyData(newWeeklyData);
    return true;
  };

  const handleMacroChange = (macro, event) => {
    const newValue = parseFloat(event.target.value) || 0;
    const newWeeklyData = { ...weeklyData };

    // Apply the change to all selected days
    selectedDays.forEach((dayIndex) => {
      const day = newWeeklyData.days[dayIndex];

      // Store original values before any changes
      const originalCarbs = day.carbs;
      const originalFat = day.fat;

      // Calculate current calories from macros
      const currentCalories =
        userProtein * 4 + originalCarbs * 4 + originalFat * 9;

      // Update the changed macro
      day[macro] = newValue;

      // Calculate the calorie difference from the macro change
      let calorieDifference = 0;
      if (macro === "carbs") {
        calorieDifference = (newValue - originalCarbs) * 4;
      } else if (macro === "fat") {
        calorieDifference = (newValue - originalFat) * 9;
      }

      // Adjust the other macro to maintain constant calories
      if (macro === "carbs" && calorieDifference !== 0) {
        // Adjust fat to compensate for carb change
        const fatAdjustment = -calorieDifference / 9;
        const newFatValue = Math.max(0, originalFat + fatAdjustment);

        // If fat would go to 0, limit carbs to what's possible with remaining calories
        if (newFatValue === 0 && originalFat > 0) {
          const maxCarbsCalories = currentCalories - userProtein * 4;
          const maxCarbsGrams = maxCarbsCalories / 4;
          day.carbs = Math.min(newValue, maxCarbsGrams);
          day.fat = 0;
        } else {
          day.fat = newFatValue;
        }
      } else if (macro === "fat" && calorieDifference !== 0) {
        // Adjust carbs to compensate for fat change
        const carbAdjustment = -calorieDifference / 4;
        const newCarbValue = Math.max(0, originalCarbs + carbAdjustment);

        // If carbs would go to 0, limit fat to what's possible with remaining calories
        if (newCarbValue === 0 && originalCarbs > 0) {
          const maxFatCalories = currentCalories - userProtein * 4;
          const maxFatGrams = maxFatCalories / 9;
          day.fat = Math.min(newValue, maxFatGrams);
          day.carbs = 0;
        } else {
          day.carbs = newCarbValue;
        }
      }
    });

    // Calculate total calorie difference across all selected days
    const totalCalorieDifference = selectedDays.reduce((sum, dayIndex) => {
      const day = newWeeklyData.days[dayIndex];
      const originalDay = weeklyData.days[dayIndex];
      const originalCalories = userProtein * 4 + originalDay.carbs * 4 + originalDay.fat * 9;
      const newCalories = userProtein * 4 + day.carbs * 4 + day.fat * 9;
      return sum + (newCalories - originalCalories);
    }, 0);

    // Redistribute the difference across other unlocked days (excluding all selected days)
    const unlockedDays = newWeeklyData.days.filter(
      (day, index) => !selectedDays.includes(index) && !day.locked
    );

    if (unlockedDays.length > 0 && totalCalorieDifference !== 0) {
      // Calculate how much to adjust each unlocked day (keep decimals for accuracy)
      const adjustmentPerDay = -totalCalorieDifference / unlockedDays.length;

      // Apply the adjustment to each unlocked day and recalculate macros
      newWeeklyData.days.forEach((day, index) => {
        if (!selectedDays.includes(index) && !day.locked) {
          const currentCalories = userProtein * 4 + day.carbs * 4 + day.fat * 9;
          const newCalories = Math.max(calorieBounds.min, currentCalories + adjustmentPerDay);
          
          // Recalculate macros for this day based on new calories while maintaining ratios
          const currentProteinCalories = userProtein * 4;
          const currentCarbsCalories = day.carbs * 4;
          const currentFatCalories = day.fat * 9;
          const currentTotalCalories =
            currentProteinCalories + currentCarbsCalories + currentFatCalories;

          if (currentTotalCalories > 0) {
            // Calculate the ratio of carbs and fat (excluding protein)
            const carbsFatCalories = currentCarbsCalories + currentFatCalories;
            const carbsRatio = currentCarbsCalories / carbsFatCalories;
            const fatRatio = currentFatCalories / carbsFatCalories;

            // Calculate new carbs and fat calories based on new total calories
            const newCarbsFatCalories = newCalories - currentProteinCalories;
            const newCarbsCalories = newCarbsFatCalories * carbsRatio;
            const newFatCalories = newCarbsFatCalories * fatRatio;

            // Update calories, carbs and fat (protein stays the same)
            day.calories = newCalories;
            day.carbs = Math.max(0, newCarbsCalories / 4);
            day.fat = Math.max(0, newFatCalories / 9);
          }
        }
      });

      // Handle any remaining difference due to floating point precision
      const totalAdjustment = adjustmentPerDay * unlockedDays.length;
      const remainingDifference = -totalCalorieDifference - totalAdjustment;

      if (remainingDifference !== 0 && unlockedDays.length > 0) {
        // Apply remaining difference to the first unlocked day
        const firstUnlockedIndex = newWeeklyData.days.findIndex(
          (day, index) => !selectedDays.includes(index) && !day.locked
        );
        if (firstUnlockedIndex !== -1) {
          const day = newWeeklyData.days[firstUnlockedIndex];
          const currentCalories = userProtein * 4 + day.carbs * 4 + day.fat * 9;
          const finalCalories = Math.max(calorieBounds.min, currentCalories + remainingDifference);
          
          // Recalculate macros for this day too
          const currentProteinCalories = userProtein * 4;
          const currentCarbsCalories = day.carbs * 4;
          const currentFatCalories = day.fat * 9;
          const currentTotalCalories =
            currentProteinCalories + currentCarbsCalories + currentFatCalories;

          if (currentTotalCalories > 0) {
            const carbsFatCalories = currentCarbsCalories + currentFatCalories;
            const carbsRatio = currentCarbsCalories / carbsFatCalories;
            const fatRatio = currentFatCalories / carbsFatCalories;

            const newCarbsFatCalories = finalCalories - currentProteinCalories;
            const newCarbsCalories = newCarbsFatCalories * carbsRatio;
            const newFatCalories = newCarbsFatCalories * fatRatio;

            day.calories = finalCalories;
            day.carbs = Math.max(0, newCarbsCalories / 4);
            day.fat = Math.max(0, newFatCalories / 9);
          }
        }
      }
    }

    setWeeklyData(newWeeklyData);
  };

  const handleReset = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Get all non-past days
    const futureDays = [];
    const futureDayIndices = [];
    
    for (let i = 0; i < weeklyData.days.length; i++) {
      const dayDate = new Date(lastWeighInDate);
      dayDate.setDate(dayDate.getDate() + i);
      dayDate.setHours(0, 0, 0, 0);
      const isPast = dayDate < today;
      
      if (!isPast) {
        futureDays.push(weeklyData.days[i]);
        futureDayIndices.push(i);
      }
    }
    
    // If no future days, nothing to reset
    if (futureDays.length === 0) {
      return;
    }
    
    // Get default macros to set directly
    const { defaultCarbs, defaultFat } = getDefaultMacrosFn();
    
    // Calculate calories from default macros
    const defaultCalories = userProtein * 4 + defaultCarbs * 4 + defaultFat * 9;
    
    const newWeeklyData = { ...weeklyData };
    
    futureDayIndices.forEach((dayIndex) => {
      const day = newWeeklyData.days[dayIndex];
      
      day.carbs = defaultCarbs;
      day.fat = defaultFat;
      day.calories = defaultCalories;
    });
    
    setWeeklyData(newWeeklyData);
  };

  const handleToggleChange = () => {
    setHighLowEveryWeek(1 - highLowEveryWeek);
  };

  const handleSave = async () => {
    if (inputErrors.length > 0) {
      return;
    }
    
    setLoading(true);

    // Days array is already ordered correctly (first day matches weekday of lastWeighInDate)
    // Format high_low_plan as array of {carbs, fat} objects
    // Keep decimals in data for accuracy
    const highLowPlan = weeklyData.days.map((day) => ({
      carbs: day.carbs,
      fat: day.fat,
    }));

    const reportDate = props.lastWeighInDate;

    // Format local_today as date string (YYYY-MM-DD)
    const localToday = toDateString(new Date());

    // Save high_low_plan and high_low_every_week via new endpoint
    saveHighLow2(
      props.account.user.id,
      props.portalId,
      reportDate,
      highLowPlan,
      highLowEveryWeek,
      localToday
    )
      .then((res) => {
        setLoading(false);
        setShow(false);
        props.loadPortalAccount();
        props.openSnackbar("Setting has been updated", false);
      })
      .catch((err) => {
        setLoading(false);
        props.openSnackbar("An error has occurred", true);
      });
  };

  return (
    <Accordion
      show={show}
      setShow={(nextShow) => {
        if (nextShow && daysAfterLastCheckIn > 7) {
          props.openSnackbar(
            "To edit high/low days, you’ll need to complete a check-in.",
            true
          );
        }
        setShow(nextShow);
      }}
      icon={SettingIcon}
      settingName="High/Low Days"
      currentSetting="Configure"
      description="Set higher or lower calorie days to redistribute your weekly macros"
      roundTop
    >
      <Container>
        <WeekContainer>
          {weeklyData.days.map((day, index) => {
            const percentages = getMacroPercentages(day, userProtein);
            const isSelected = selectedDays.includes(index);
            const isPrimarySelected = selectedDays.length > 0 && selectedDays[0] === index;
            
            const dayDate = new Date(lastWeighInDate);
            dayDate.setDate(dayDate.getDate() + index);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            dayDate.setHours(0, 0, 0, 0);
            const isPast = dayDate < today;
            
            // Checkbox is enabled if: not past, and (no days selected, or this is the primary selected day, or this day's calories match the primary selected day)
            // Also disable if 6 days are already selected and this day is not already selected
            const canMultiSelect = !isPast && (selectedDays.length === 0 || isPrimarySelected || doesDayMatchSelectedCalories(index, selectedDays, weeklyData)) 
              && (selectedDays.length < 6 || isSelected);
            
            // Format date as "Jan 6" format
            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const dateLabel = `${monthNames[dayDate.getMonth()]} ${dayDate.getDate()}`;
            
            return (
              <DayColumn
                key={index}
                day={day}
                index={index}
                userProtein={userProtein}
                percentages={percentages}
                isSelected={isSelected}
                canMultiSelect={canMultiSelect}
                onDaySelect={handleDaySelect}
                onCheckboxToggle={handleCheckboxToggle}
                onDayLock={handleDayLock}
                getMacroFlexValue={getMacroFlexValue}
                isPast={isPast}
                dateLabel={dateLabel}
              />
            );
          })}
        </WeekContainer>
        <ResetContainer>
          <ResetButton onClick={handleReset}>
            Reset High/Low Days
            <RefreshIcon style={{ fontSize: 16 }} />
          </ResetButton>
        </ResetContainer>
        <Toggle
          label="Repeat High/Low Days"
          checked={highLowEveryWeek === 1}
          onChange={handleToggleChange}
          value="highLowEveryWeek"
          helpTip="Turning this setting on will re-apply these settings after your check-in. The exact macros each day may differ based on changes to your weekly baseline macros, but the pattern of distribution will remain."
        />

        {(() => {
          // Calculate available days (not past, not locked)
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const availableDays = [];
          for (let i = 0; i < weeklyData.days.length; i++) {
            const dayDate = new Date(lastWeighInDate);
            dayDate.setDate(dayDate.getDate() + i);
            dayDate.setHours(0, 0, 0, 0);
            const isPast = dayDate < today;
            if (!isPast && !weeklyData.days[i].locked) {
              availableDays.push(i);
            }
          }
          
          // Check if all available days are selected
          const allAvailableDaysSelected = availableDays.length > 0 && 
            availableDays.every(dayIndex => selectedDays.includes(dayIndex));
          
          const caloriesDisabled = selectedDays.length === 0 || 
            selectedDays.some(i => weeklyData.days[i].locked) ||
            allAvailableDaysSelected;
          
          return (
            <ControlGroup $reducedOpacity={selectedDays.length === 0 || allAvailableDaysSelected}>
              <ControlLabel>
                <Label>Calories</Label>
                <ValueInput
                  min={displayedCalorieBounds.min}
                  max={displayedCalorieBounds.max}
                  step={1}
                  value={
                    calorieInput !== null
                      ? calorieInput
                      : Math.round(
                          selectedDays.length === 1
                            ? currentDay.calories
                            : getSliderValue("calories", selectedDays, weeklyData, selectedDaysValues, getDefaultMacrosFn, props.account.user)
                        )
                  }
                  onChange={(e) => handleInput("calories", e.target.value)}
                  disabled={caloriesDisabled}
                  $reducedOpacity={selectedDays.length === 0 || allAvailableDaysSelected}
                />
              </ControlLabel>
              <div style={{ padding: "0 8px 0 8px", opacity: (selectedDays.length === 0 || allAvailableDaysSelected) ? 0.4 : 1, transition: "opacity 0.2s ease" }}>
                <CalorieSlider
                  min={displayedCalorieBounds.min}
                  max={displayedCalorieBounds.max}
                  step={1}
                  value={Math.min(displayedCalorieBounds.max, Math.max(displayedCalorieBounds.min, getSliderValue("calories", selectedDays, weeklyData, selectedDaysValues, getDefaultMacrosFn, props.account.user)))}
                  onChange={(e, val) => handleSlider("calories", val)}
                  onChangeCommitted={handleCalorieSliderCommit}
                  disabled={caloriesDisabled}
                  valueLabelDisplay="off"
                  aria-label="calorie-slider"
                />
              </div>
            </ControlGroup>
          );
        })()}

        <ControlGroup $reducedOpacity={selectedDays.length === 0}>
          <ControlLabel>
            <Label>Carbs</Label>
            <ValueInput
              min={getMinCarbs(selectedDays, weeklyData, currentDay, selectedDaysValues, userProtein, getDefaultMacrosFn, props.account.user)}
              step={1}
              value={
                carbsInput !== null
                  ? carbsInput
                  : Math.round(
                      selectedDays.length === 1
                        ? currentDay.carbs
                        : getSliderValue("carbs", selectedDays, weeklyData, selectedDaysValues, getDefaultMacrosFn, props.account.user)
                    )
              }
              onChange={(e) => handleInput("carbs", e.target.value)}
              disabled={selectedDays.length === 0 || selectedDays.some(i => weeklyData.days[i].locked)}
              $reducedOpacity={selectedDays.length === 0}
            />
          </ControlLabel>
          <div style={{ padding: "0 8px 0 8px", opacity: selectedDays.length === 0 ? 0.4 : 1, transition: "opacity 0.2s ease" }}>
            <MacroSlider
              min={getMinCarbs(selectedDays, weeklyData, currentDay, selectedDaysValues, userProtein, getDefaultMacrosFn, props.account.user)}
              max={Math.round(getMaxCarbs(selectedDays, weeklyData, currentDay, selectedDaysValues, userProtein, getDefaultMacrosFn, props.account.user))}
              step={1}
              value={getSliderValue("carbs", selectedDays, weeklyData, selectedDaysValues, getDefaultMacrosFn, props.account.user)}
              onChange={(e, val) => handleSlider("carbs", val)}
              disabled={selectedDays.length === 0 || selectedDays.some(i => weeklyData.days[i].locked)}
              valueLabelDisplay="off"
              aria-label="carbs-slider"
            />
          </div>
        </ControlGroup>

        <ControlGroup $reducedOpacity={selectedDays.length === 0}>
          <ControlLabel>
            <Label>Fat</Label>
            <ValueInput
              min={Math.round(getMinFat(selectedDays, weeklyData, currentDay, selectedDaysValues, getDefaultMacrosFn, props.account.user))}
              step={1}
              value={
                fatInput !== null
                  ? fatInput
                  : Math.round(
                      selectedDays.length === 1
                        ? currentDay.fat
                        : getSliderValue("fat", selectedDays, weeklyData, selectedDaysValues, getDefaultMacrosFn, props.account.user)
                    )
              }
              onChange={(e) => handleInput("fat", e.target.value)}
              disabled={selectedDays.length === 0 || selectedDays.some(i => weeklyData.days[i].locked)}
              $reducedOpacity={selectedDays.length === 0}
            />
          </ControlLabel>
          <div style={{ padding: "0 8px 0 8px", opacity: selectedDays.length === 0 ? 0.4 : 1, transition: "opacity 0.2s ease" }}>
            <FatSlider
              min={Math.round(getMinFat(selectedDays, weeklyData, currentDay, selectedDaysValues, getDefaultMacrosFn, props.account.user))}
              max={Math.round(getMaxFat(selectedDays, weeklyData, currentDay, selectedDaysValues, userProtein, getDefaultMacrosFn, props.account.user))}
              step={1}
              value={getSliderValue("fat", selectedDays, weeklyData, selectedDaysValues, getDefaultMacrosFn, props.account.user)}
              onChange={(e, val) => handleSlider("fat", val)}
              disabled={selectedDays.length === 0 || selectedDays.some(i => weeklyData.days[i].locked)}
              valueLabelDisplay="off"
              aria-label="fat-slider"
            />
          </div>
        </ControlGroup>

        {inputErrors.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              marginTop: "16px",
            }}
          >
            {inputErrors.map((error, index) => (
              <ErrorBox key={index}>
                <ErrorIcon>!</ErrorIcon>
                <span>{error}</span>
              </ErrorBox>
            ))}
          </div>
        )}

        <ButtonRow>
          <SaveButton
            disabled={isLoading || inputErrors.length > 0}
            isLoading={isLoading}
            onClick={handleSave}
          />
        </ButtonRow>
      </Container>
    </Accordion>
  );
};

export default UpdateHighLow;
